import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommandResult, Department, DepartmentGroup } from '@cl/shared/models';
import { BaseServiceWithFilter } from './base.service';
import { DepartmentFilter } from '@cl/shared/models/departments/department-filter.model';
import { Observable, lastValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DepartmentsService extends BaseServiceWithFilter<Department, DepartmentFilter> {
    constructor(httpClient: HttpClient) {
        super(httpClient, '/api/v1/departments/');
    }

    async getGroupedDepartments(): Promise<DepartmentGroup[]> {
        const departments = (await this.getAll().toPromise()).data;
        return this.createDepartmentGroups(departments);
    }

    async getAuthorizedGroupedDepartments(permission: number): Promise<DepartmentGroup[]> {
        const departments = await this.httpClient.get<Department[]>(`${this.baseApiUrl}authorized/${permission}`).toPromise();
        return this.createDepartmentGroups(departments);
    }

    async getAccessibleGroupedDepartments(): Promise<DepartmentGroup[]> {
        const result = await lastValueFrom(this.httpClient.get<CommandResult<Department[]>>(`${this.baseApiUrl}accessible`));
        return this.createDepartmentGroups(result.data);
    }

    getAuthorizedDepartments(permission: number): Observable<Department[]> {
        return this.httpClient.get<Department[]>(`${this.baseApiUrl}authorized/${permission}`);
    }

    private createDepartmentGroups(departments: Department[]) {
        const departmentGroup = [];
        departments.forEach((d) => {
            let group = departmentGroup.find((gd) => gd.groupName === d.companyName);
            if (group) {
                group.departments.push(d);
                return;
            }
            group = {
                groupName: d.companyName,
                departments: [d],
            } as DepartmentGroup;
            departmentGroup.push(group);
        });
        return departmentGroup;
    }

    searchBetweenDepartmentsRelatedToUser(text: string): Observable<CommandResult<Department[]>> {
        return this.httpClient.get<CommandResult<Department[]>>(`${this.baseApiUrl}search-related/${text}`);
    }

    toString(object: Department): string {
        return `${object.name} (${object.companyName})`;
    }
}
